import { Component, inject, signal, ViewEncapsulation } from "@angular/core";
import { CurrencyPipe } from "@angular/common";
import { DynamicFiltersComponent } from "../../components/dynamic-filters/dynamic-filters.component";
import { ConfirmationService, PrimeTemplate } from "primeng/api";
import { Router, ActivatedRoute, RouterLink } from "@angular/router";
import { AbstractListController } from "src/app/common/abstract-list.component";
import { Bookings } from "src/app/models/bookings";
import { BookingsService } from "src/app/services/bookings.service";
import {
  PAIED_TYPE_LIST,
  PARAM_BOOKINGS_PATH,
  STATUS_TYPE_LIST,
} from "../../constants/constants";
import { ClientsService } from "src/app/services/clients.service";
import { Clients } from "src/app/models/clients";
import { RoomsService } from "src/app/services/rooms.service";
import { Bind } from "primeng/bind";
import { Drawer } from "primeng/drawer";

import { FormsModule } from "@angular/forms";
import { InputText } from "primeng/inputtext";
import { Select } from "primeng/select";
import { DatePicker } from "primeng/datepicker";
import { ButtonDirective } from "primeng/button";
import { TableModule } from "primeng/table";
import { Tag } from "primeng/tag";
import { NgxPaginationModule } from "ngx-pagination";
import { BookingDetailComponent } from "../../components/booking-detail/booking-detail.component";
import { ConfirmDialog } from "primeng/confirmdialog";
import { OnlyCalendarComponent } from "../../components/calendar/calendar.component";
import { TranslatePipe } from "@ngx-translate/core";

import { TPipe } from "../../i18n/t.pipe";
import { opt } from "../../i18n/i18n";
const YES_NO = [
  opt("no", "0"),
  opt("sì", "1"),
];

@Component({
    standalone: true,
    selector: "app-manage-bookings",
    templateUrl: "./manage-bookings.component.html",
    styleUrl: "./manage-bookings.component.css",
    encapsulation: ViewEncapsulation.None,
    imports: [TPipe, Bind, Drawer, PrimeTemplate, FormsModule, InputText, Select, DatePicker, ButtonDirective, RouterLink, TableModule, Tag, NgxPaginationModule, BookingDetailComponent, ConfirmDialog, OnlyCalendarComponent, TranslatePipe, CurrencyPipe, DynamicFiltersComponent]
})
export class ManageBookingsComponent extends AbstractListController<
  Bookings,
  any
> {
  readonly roomsType = signal<any[]>([]);
  protected bookingsService: BookingsService;
  private clientService = inject(ClientsService);
  private roomsService = inject(RoomsService);
  protected confirmationService: ConfirmationService;

  title = "Prenotazioni";
  readonly cols = signal<any[]>(undefined);
  readonly columnOptions = signal<any[]>(undefined);
  readonly selectedColumns = signal<any[]>(undefined);
  readonly roomDialog = signal<boolean>(false);
  readonly bookedDialog = signal<boolean>(false);
  readonly booking = signal<Bookings>(undefined);
  readonly calendarDialog = signal<boolean>(false);
  readonly client = signal<Clients>(undefined);
  readonly selectedWorks = signal<any[]>(undefined);
  isLoading: boolean;
  constructor() {
    const bookingsService = inject(BookingsService);
    const confirmationService = inject(ConfirmationService);
    const router = inject(Router);
    const route = inject(ActivatedRoute);

    super(
      bookingsService,
      confirmationService,
      router,
      route,
      PARAM_BOOKINGS_PATH,
    );
    this.bookingsService = bookingsService;
    this.confirmationService = confirmationService;

    this.cols.set([
      { field: "is_booked", header: "Status", type: "string" },
      { field: "date_from", header: "Dal" },
      { field: "date_to", header: "Al" },
      { field: "roomId", header: "Camera" },
      { field: "email", header: "Ospite" },
      { field: "final_price", header: "Prezzo" },
      { field: "is_paid", header: "Pagato", type: "string" },
      { field: "date", header: "Data", type: "string" },
      { field: "adults", header: "Adulti" },
      { field: "children", header: "Bambini" },
      { field: "breakfast", header: "Colazione" },
      { field: "is_active", header: "Attivo" },
      { field: "created_at", header: "Creata il" },
    ]);

    const paramsPartnerType = {};
    this.roomsService.list(paramsPartnerType).subscribe((pData) => {
      this.roomsType.set([
        opt("Nessuno", null),
        ...pData.map((type: any) => ({
          label: type.title,
          value: type.id,
        })),
      ]);

      this.colsFilter.set([
        {
          field: "is_booked", icon: "pi-check-circle",
          header: "Status",
          type: "select",
          option: STATUS_TYPE_LIST,
        },
        { field: "date_from", icon: "pi-calendar", header: "Dal", type: "date" },
        { field: "date_to", icon: "pi-calendar", header: "Al", type: "date" },
        {
          field: "roomId", icon: "pi-home",
          header: "Camera",
          type: "select",
          option: this.roomsType(),
        },
        { field: "email", icon: "pi-user", header: "Ospite", type: "string" },
        {
          field: "is_paid", icon: "pi-wallet",
          header: "Pagato",
          type: "select",
          option: PAIED_TYPE_LIST,
        },
        { field: "adults", icon: "pi-users", header: "Adulti", type: "number" },
        { field: "children", icon: "pi-users", header: "Bambini", type: "number" },
        { field: "final_price", icon: "pi-euro", header: "Prezzo", type: "number" },
        { field: "breakfast", icon: "pi-star", header: "Colazione", type: "select", option: YES_NO },
        { field: "is_active", icon: "pi-power-off", header: "Attivo", type: "select", option: YES_NO },
        { field: "created_at", icon: "pi-clock", header: "Creata il", type: "date" },
      ]);
    });
  }

  initials(b: any): string {
    const name = b.client ? `${b.client.name} ${b.client.surname}` : `${b.name || ""} ${b.surname || ""}`;
    return name.split(" ").filter(Boolean).slice(0, 2).map((w: string) => w[0]).join("").toUpperCase() || "?";
  }

  nightsOf(b: any): number {
    const n = Math.round((new Date(b.date_to).getTime() - new Date(b.date_from).getTime()) / 86400000);
    return n > 0 ? n : 0;
  }

  bookingCalendar() {
    this.calendarDialog.set(true);
  }

  ngOnInit(): void {
    super.ngOnInit();                       // reads page and filters from the URL, then loads
  }

  editRoom(booking: any) {
    this.bookedDialog.set(true);
    const bookingId = booking.id;
    this.bookingsService.getId(bookingId).subscribe((res) => {
      this.booking.set(res);
    });
  }

  edit(booking: Bookings) {
    this.booking.set({ ...booking });

    this.clientService.getId(this.booking().client_id).subscribe((res) => {
      this.client.set(res);
    });

    this.selectedWorks.set(this.booking().works_id.split(","));
    this.roomDialog.set(true);
  }
}
